import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { createApiCall } from 'services';
import { useDispatch } from 'store/hooks';
import { ActionType } from 'store/certificates/model';
import { CertificateCustomer } from 'types';
import { Dispatch, useContext } from 'react';
import { Action, ActionCallback, Nullable } from 'store/model';
import { ICertificatesReducer } from 'store/certificates/reducer';
import { PreviewContext } from 'contexts/previewContext';
import { certificatesMock } from 'mocks/certificatesMock';
import { applyPreviewProduct } from 'mocks/applyPreviewProduct';
import type { StudioPreviewProduct } from '@repo/types';
import { ICertificateShortInfo, ICertificateFullInfo } from '@repo/types';

const getCertificatePublic = async (
  certificateId: string,
  dispatch: Dispatch<Action<Nullable<CertificateCustomer>>>,
  isPreview: boolean,
  previewProduct: StudioPreviewProduct | null
) => {
  // Preview with NO certificate id is Studio previewing a THEME — stable filler is
  // correct there. Preview WITH an id is a brand looking at one of its own certificates
  // before activating it, so it must fetch the real record. `/certificates/public/:id`
  // is @OptionalAuth and applies no activation filter, so this works unauthenticated and
  // for a not-yet-activated certificate — which is the entire point: activation writes a
  // metering row, and paying to look at your own work is indefensible.
  //
  // It returns the anonymous SHORT payload here, which is also what we want: the brand is
  // asking "what will the public see", not "what does the owner see".
  // VRG-827: the mock is the SCAFFOLDING; the product inside it is the brand's own
  // when Studio has sent one. `applyPreviewProduct` falls back field by field, so a
  // brand with no products — or a product with no image — gets exactly the previous
  // behaviour rather than a blank.
  if (isPreview && !certificateId) return applyPreviewProduct(certificatesMock[0], previewProduct);

  const response = await createApiCall({
    url: `/certificates/public/${certificateId}`,
    throwError: true,
  });

  dispatch({
    type: ActionType.SET_CERTIFICATE,
    payload: response.data as CertificateCustomer,
  });

  return response.data;
};

/**
 * The already-loaded row for this certificate, from whichever `['certificates', ...]`
 * list query has it cached.
 *
 * Why: tapping a row from the list used to drop the user on a FULL-SCREEN Vera
 * loader, even though the list had just rendered that certificate's name, image
 * and status a moment earlier. Showing a spinner for data already on the previous
 * screen is the thing that makes a web app feel like a web app.
 *
 * Read across ALL list queries rather than one key, because the key carries the
 * user's filter/sort/tab params (`['certificates', params]`) — there is no single
 * key to look under, and the row may have come from any of them.
 */
const findInListCache = (
  queryClient: QueryClient,
  certificateId: string,
): ICertificateFullInfo | undefined => {
  if (!certificateId) return undefined;
  for (const [, data] of queryClient.getQueriesData({ queryKey: ['certificates'] })) {
    // The response shape has moved around; accept either a bare array or a
    // paginated envelope rather than coupling this to one of them.
    const rows: unknown =
      Array.isArray(data) ? data : (data as { data?: unknown } | undefined)?.data;
    if (!Array.isArray(rows)) continue;
    const hit = rows.find(r => (r as { id?: string } | null)?.id === certificateId);
    if (hit) return hit as ICertificateFullInfo;
  }
  return undefined;
};

export const useGetCertificatePublic = (certificateId: string) => {
  const { isPreview, previewCertificateId, previewProduct } = useContext(PreviewContext);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  // On the preview route there is no `:certificateId` path param, so the id arrives on
  // the context from `?certificate_id=` instead. Falling back rather than overriding
  // keeps the normal route authoritative.
  const effectiveId = certificateId || previewCertificateId || '';

  return useQuery({
    // The id is part of the key, so a mock preview and a real-certificate preview cannot
    // serve each other's cached result.
    // `previewProduct?.id` is in the key so choosing a different product in Studio
    // re-renders. Without it the mock branch is a pure cache hit on a constant key and
    // the selector would appear to do nothing — the selection changes no input the
    // query can see. The id, not the object: a new object identity on every render
    // would refetch forever.
    queryKey: ['certificate-public', effectiveId, previewProduct?.id ?? null],
    queryFn: () => getCertificatePublic(effectiveId, dispatch, isPreview, previewProduct),
    // On the preview route BOTH `isPreview` and the id are set by effects, so the first
    // render has neither and would otherwise request `/certificates/public/` and 404.
    // (That spurious call predates the preview id — it used to request `.../undefined`.)
    // Once either is known the query is meaningful: an id means fetch it, isPreview with
    // no id means serve the mock.
    enabled: !!effectiveId || isPreview,
    /**
     * `placeholderData`, NOT `initialData`.
     *
     * The list row is a SHORT info shape; the page wants the full one. `initialData`
     * would write that partial object into the cache as if it were a real response
     * and mark it fresh, so the missing fields could persist. `placeholderData`
     * never enters the cache and always refetches — the page paints immediately
     * with the fields the list already had, and fills in the rest when the request
     * lands. `isPlaceholderData` distinguishes the two for any caller that cares.
     *
     * This deliberately does NOT cover the cold path. Arriving by NFC tap or a
     * shared link has no list cache, nothing to show, and therefore keeps the
     * loader — which is the one case where a loader is honest.
     *
     * Keyed off `effectiveId`, not `certificateId`: the preview route carries its
     * id on the context instead of the path, and looking up the wrong one would
     * silently return no placeholder there.
     */
    placeholderData: () => findInListCache(queryClient, effectiveId),
  });
};
